import axios from "axios";
import cloneDeep from "lodash.clonedeep";
import { toast } from "react-toastify";
import { useEffect, useRef } from "react";
import { Updater } from "use-immer";
import { useImmer } from "use-immer";
import { v4 } from "uuid";

import {
  AnalyticsProductDto,
  ExperienceCategory,
  ExperienceDto,
  ExperienceProductDto,
  ExperienceType,
  GetScriptStatusResponseBodyType,
  OfferProductDto,
  ProductVariantProductType,
  PutExperienceRequestBodyType,
  PutExperienceResponseBodyType,
  RedirectDto,
  S3FileLocationDto,
  TabEnumFe,
  TabType,
} from "@intelligems/ig-types";
import { officialCategoryName } from "@intelligems/ig-utils";
import * as Sentry from "@sentry/react";
import { getUserInterfacesFromExperienceDto } from "@src/api/formatters/from-dto/user-interfaces";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useGetProcesses } from "@src/api/processes/get-processes";
import { useUploadToPresignedUrl } from "@src/api/organizations/upload-to-presigned-url";
import {
  exclusionGroupKeys,
  experienceKeys,
  historyKeys,
  invalidateModuleExperienceListQueries,
  offerKeys,
  redirectKeys,
  setExperienceQueryData,
  updateInExperiencesCache,
  updateInExperiencesListCache,
} from "@src/api/queryKeys";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { useLoggedInUser } from "@src/api/user-auths/login";
import { usePutWidget } from "@src/api/widgets/put-widget";
import { useDialogs } from "@src/hooks/dialog";
import { useMarketSelections } from "@src/features/experiments/pages/shippingExperience/components/MarketShipping/useMarketSelections";
import { getMutationErrorMessage } from "@src/queryClient";
import store, { AppDispatch, useDispatch, useSelector } from "@src/store";
import { processSavedAgProducts } from "@src/store/slices/agProducts";
import { processSavedAnalyticsProducts } from "@src/store/slices/analyticsProducts";
import { initAudiences } from "@src/store/slices/audiences";
import {
  initCartTargetingFromVariations,
  processStoredCartTargeting,
} from "@src/store/slices/cartTargeting";
import { initCheckoutBlocksFromVariations } from "@src/store/slices/checkoutBlocks";
import { setAlertOpen } from "@src/store/slices/experienceInfoState";
import {
  initCustomMetrics,
  initKeyMetrics,
} from "@src/store/slices/experienceMetrics/experienceMetrics";
import { processSavedExperienceProducts } from "@src/store/slices/experienceProducts";
import { initExperienceProductTargeting } from "@src/store/slices/experienceProductTargeting";
import {
  initExperimentFromDto,
  initPersonalizationFromDto,
  selectedExperience,
  setExperienceId,
  setHasClickedSave,
  setShippingSaveId,
} from "@src/store/slices/experiences";
import { getPutExperiencePayload } from "@src/store/slices/experiences/selectors/payload/getPutExperiencePayload";
import { getExperienceProductsAction } from "@src/store/slices/experiences/selectors/products/getExperienceProductsAction";
import { ProductsAction } from "@src/store/slices/experiences/types";
import { setIgnoreUpdateDetected } from "@src/store/slices/global";
import { initOffersFromVariations } from "@src/store/slices/offers";
import { initOnsiteEdits } from "@src/store/slices/onsiteEdits";
import { initComponents } from "@src/store/slices/experienceComponents";
import { initOnsiteInjections } from "@src/store/slices/onsiteInjections";
import { initPageTargeting } from "@src/store/slices/pageTargeting";
import { initRedirectsFromVariationsState } from "@src/store/slices/redirects";
import { processStoredUpsellProductRules } from "@src/store/slices/upsellProductRules";
import { initUserInterfaces } from "@src/store/slices/userInterfaces";
import {
  ensureRateGroupOrder,
  initVariations,
} from "@src/store/slices/variations";
import { selectedWidget } from "@src/store/slices/widgets";
import { getAuthToken } from "@src/utils";
import {
  generatePreviewLink,
  getPreviewThemeId,
} from "@src/utils/generatePreviewLink";
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "../api";
import { getExperience } from "./get-experience";
import { useConfiguration } from "../configurations/use-configuration";
import { getOnsiteEditsFromExperienceDto } from "../formatters/from-dto/onsite-edits";
import { getComponentsFromExperienceDto } from "@src/api/formatters/from-dto/components";
import { getOnsiteInjectionsFromExperienceDto } from "../formatters/from-dto/onsite-injections";
import { usePostCustomShippingRate } from "../organization-actions/post-custom-shipping-rates";
import { usePostSubscriptionProducts } from "../organization-actions/post-subscription-products";
import { useGetS3PresignedUploadUrl } from "../organizations/get-s3-presigned-upload-url";

/** Invalidates all personalization list queries (for new experiences that need to be added to lists) */
const invalidateAllPersonalizationQueries = async (
  queryClient: QueryClient
) => {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: experienceKeys.homeOverviewPersonalizationList,
    }),
    queryClient.invalidateQueries({
      queryKey: experienceKeys.detailedPersonalizationList,
    }),
    queryClient.invalidateQueries({
      queryKey: experienceKeys.offerPersonalizationList,
    }),
    queryClient.invalidateQueries({
      queryKey: experienceKeys.checkoutPersonalizationList,
    }),
    queryClient.invalidateQueries({
      queryKey: experienceKeys.postPurchasePersonalizationList,
    }),
  ]);
};

/** Updates existing experience in caches in-place (no refetch) */
const updatePersonalizationCaches = (
  queryClient: QueryClient,
  experience: ExperienceDto
) => {
  // Update main caches in-place (no refetch)
  updateInExperiencesCache(
    queryClient,
    experienceKeys.detailedPersonalizationList,
    experience
  );
  updateInExperiencesListCache(
    queryClient,
    experienceKeys.homeOverviewPersonalizationList,
    experience
  );

  // Update offer-related caches if this is an offer experience
  if (experience.testTypes.hasTestCampaign) {
    updateInExperiencesCache(
      queryClient,
      experienceKeys.offerPersonalizationList,
      experience
    );
    void queryClient.invalidateQueries({ queryKey: offerKeys.all });
    void queryClient.invalidateQueries({
      queryKey: offerKeys.experiencesList,
    });
  }
  // Update checkout-related caches if this is a checkout experience
  if (experience.testTypes.hasTestCheckoutBlocks) {
    updateInExperiencesCache(
      queryClient,
      experienceKeys.checkoutPersonalizationList,
      experience
    );
  }
  // Update post-purchase-related caches if this is a post-purchase experience
  if (experience.testTypes.hasTestPostPurchase) {
    updateInExperiencesCache(
      queryClient,
      experienceKeys.postPurchasePersonalizationList,
      experience
    );
  }
};

const stateReInitHelper = (
  experience: ExperienceDto,
  dispatch: AppDispatch,
  defaultCurrencyCode: string | undefined
) => {
  dispatch(setExperienceId(experience.id));

  const currentUrl = new URL(window.location.href);
  if (experience.category === "experiment") {
    currentUrl.pathname = `/experiment/${experience.id}`;
  } else {
    currentUrl.pathname = `/personalization/${experience.id}`;
  }

  currentUrl.searchParams.set("action", "edit");
  window.history.replaceState({}, "", currentUrl);

  if (experience.category === "experiment") {
    dispatch(
      initExperimentFromDto({
        experience,
        testFlowType: experience.type,
        defaultCurrency:
          experience.type === "pricing"
            ? defaultCurrencyCode || "USD"
            : undefined,
        tab:
          (currentUrl.searchParams.get("tab") as TabType) ??
          TabEnumFe.TEST_GROUPS,
      })
    );

    if (experience.type === "shipping") {
      dispatch(ensureRateGroupOrder());
    }
  } else {
    dispatch(initPersonalizationFromDto({ experience }));
  }
  dispatch(
    initOnsiteEdits(getOnsiteEditsFromExperienceDto(experience.variations))
  );
  dispatch(
    initComponents(getComponentsFromExperienceDto(experience.variations))
  );
  dispatch(initVariations({ variations: experience.variations }));
  dispatch(
    initOffersFromVariations({
      variations: experience.variations,
      isInitialize: true,
    })
  );
  dispatch(
    initPageTargeting({
      pageTargeting: experience.experiencePageTargeting,
    })
  );
  dispatch(
    initExperienceProductTargeting({
      experienceProductTargeting: experience.experienceProductTargeting,
    })
  );
  dispatch(initAudiences({ audience: experience.audience }));
  dispatch(
    initRedirectsFromVariationsState({
      variations: experience.variations,
    })
  );
  dispatch(
    initOnsiteInjections(
      getOnsiteInjectionsFromExperienceDto(experience.variations)
    )
  );
  dispatch(initUserInterfaces(getUserInterfacesFromExperienceDto(experience)));

  dispatch(
    initKeyMetrics({
      keyMetrics: experience.experienceKeyMetrics,
    })
  );

  dispatch(
    initCustomMetrics({
      customMetrics: experience.experienceCustomMetrics,
    })
  );
  dispatch(
    initCheckoutBlocksFromVariations({
      variations: experience.variations,
    })
  );
  dispatch(
    initCartTargetingFromVariations({
      variations: experience.variations,
    })
  );
  dispatch(processStoredUpsellProductRules());
  dispatch(processStoredCartTargeting());
  dispatch(processSavedAgProducts());
  dispatch(processSavedAnalyticsProducts());
  dispatch(processSavedExperienceProducts());
};

const verifyPersonalizationAnalyticsViewType = (
  data: PutExperienceRequestBodyType,
  productType?: ProductVariantProductType
) => {
  // only apply this logic to personalization experiences
  if (data.experience.category === "personalization") {
    // if you have offer products or price mod, default to test orders only
    if (productType === "price" || productType === "offer") {
      data.experience.experienceAnalysis.analyticsViewType = "test_orders_only";
    } else {
      // otherwise, default should be sitewide
      data.experience.experienceAnalysis.analyticsViewType = "sitewide_orders";
    }
  }
};

type UploadProductsToS3Fn = (params: {
  products: (ExperienceProductDto | OfferProductDto | AnalyticsProductDto)[];
  category: ExperienceCategory;
}) => Promise<S3FileLocationDto>;

const putExperience = async ({
  _data,
  productsAction,
  uploadProductsToS3,
}: {
  _data: PutExperienceRequestBodyType;
  productsAction?: ProductsAction;
  uploadProductsToS3: UploadProductsToS3Fn;
}): Promise<PutExperienceResponseBodyType> => {
  const data: PutExperienceRequestBodyType = cloneDeep(_data);
  switch (productsAction?.type) {
    case "process": {
      data.experience.productVariantAction = {
        type: productsAction.productType,
        location: productsAction.location,
      };
      break;
    }
    case "upload": {
      const location = await uploadProductsToS3({
        products: productsAction.products,
        category: data.experience.category,
      });
      if (location) {
        data.experience.productVariantAction = {
          type: productsAction.productType,
          location,
        };
      }
      break;
    }
  }

  verifyPersonalizationAnalyticsViewType(data, productsAction?.productType);

  sanitizeData(data);
  const url = data.experience.id
    ? `/v2/experiences/${data.experience.id}`
    : "/v2/experiences";

  const resp = await api.put<
    PutExperienceResponseBodyType,
    PutExperienceRequestBodyType
  >(url, data);

  return resp.data;
};

interface UsePutExperienceOptions {
  rateDescription?: string | null | undefined;
  hasSubscriptionProducts?: boolean;
  testType?: ExperienceType;
  redirectsState?: RedirectDto[];
  launchEditor?: boolean;
  setLaunchEditor?: Updater<boolean>;
  scriptStatus?: GetScriptStatusResponseBodyType | null;
}

export const usePutExperience = (
  {
    rateDescription,
    hasSubscriptionProducts,
    testType,
    redirectsState,
    launchEditor,
    scriptStatus,
    setLaunchEditor,
  }: UsePutExperienceOptions = { hasSubscriptionProducts: false }
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const org = useGetOrg();
  const user = useLoggedInUser();
  const isAdmin = useIsAdmin();
  const productsAction = useSelector(getExperienceProductsAction);
  const hasNewVisualBuilderMod = useSelector(
    (state: { onsiteEdits: { hasNewVisualBuilderMod: boolean } }) =>
      state.onsiteEdits.hasNewVisualBuilderMod
  );
  const { openDialog } = useDialogs();

  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const postCustomShippingRate = usePostCustomShippingRate();
  const postSubscriptionProducts = usePostSubscriptionProducts();
  const { mutateAsync: uploadProductsToS3 } = useUploadProductsToS3();

  if (testType?.includes("content") && redirectsState === undefined) {
    Sentry.captureMessage("Missing Redirect State");
  }

  return useMutation<
    PutExperienceResponseBodyType,
    Error,
    PutExperienceRequestBodyType
  >({
    mutationKey: ["mutatePutExperience"],
    mutationFn: async (data: PutExperienceRequestBodyType) => {
      return putExperience({
        _data: {
          ...data,
          containsNewVisualBuilderMod:
            data.containsNewVisualBuilderMod || hasNewVisualBuilderMod,
        },
        productsAction,
        uploadProductsToS3,
      });
    },
    onMutate: () => {
      dispatch(setIgnoreUpdateDetected(true));
    },
    onSuccess: async (data, payload) => {
      dispatch(setHasClickedSave(false));
      dispatch(setAlertOpen({ value: false }));
      setExperienceQueryData(queryClient, data.experience);
      invalidateModuleExperienceListQueries(queryClient);

      if (data.experience.category === "experiment") {
        await queryClient.invalidateQueries({
          queryKey: experienceKeys.experimentList,
        });
      } else if (data.experience.category === "personalization") {
        const isNewExperience = !payload.experience.id;
        if (isNewExperience) {
          // New experiences need to be added to lists, so invalidate all
          await invalidateAllPersonalizationQueries(queryClient);
        } else {
          // Existing experiences can be updated in-place
          updatePersonalizationCaches(queryClient, data.experience);
        }
      }

      await queryClient.invalidateQueries({ queryKey: exclusionGroupKeys.all });
      queryClient.setQueryData(redirectKeys.byExperience(data.experience.id), {
        redirects: payload.redirects,
      });
      void queryClient.invalidateQueries({
        queryKey: historyKeys.one(data.experience.id),
      });

      stateReInitHelper(
        data.experience,
        dispatch,
        storeCurrencyConfig?.value?.code
      );

      if (launchEditor) {
        if (!scriptStatus?.exists) {
          openDialog("redirectToPlugin", {
            id: data.experience.id,
            experience: data.experience,
          });
        } else {
          const token = getAuthToken();
          const url = generatePreviewLink({
            organization: org.data.org,
            previewThemeId: getPreviewThemeId(
              payload.redirects.filter((redirect) => redirect.isThemeTest)
            ),
            experience: data.experience,
            authToken: token,
            builderModeEntity:
              data.experience.type === "personalization"
                ? "personalization"
                : "experiment",
          });

          window.open(url, "_blank");
          openDialog("onsiteEditRefresh");
          if (setLaunchEditor) {
            setLaunchEditor(false);
          }
        }
      }

      if (data.experience.testTypes.hasTestPricing) {
        if (hasSubscriptionProducts && !isAdmin) {
          postSubscriptionProducts.mutate({
            body: {
              shopId: org.data.org.shopId!,
              performingUserEmail: user.data?.user?.email,
              subscriptionId: org.data.org.subscriptionId ?? undefined,
            },
            pathParams: {
              experienceId: data.experience.id,
            },
            queryParams: undefined,
          });
        }
      }
      if (data.experience.testTypes.hasTestShipping && rateDescription) {
        postCustomShippingRate.mutate({
          body: {
            shopId: org.data.org.shopId!,
            performingUserEmail: user.data?.user?.email,
            rateDescription: rateDescription,
          },
          pathParams: {
            experienceId: data.experience.id,
          },
          queryParams: undefined,
        });
      }
      toast.success(
        `${officialCategoryName(data.experience.category, true)} Saved Successfully`
      );
    },
    onError: async (error, payload) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 503) {
          if (payload.experience.id) {
            setExperienceQueryData(queryClient, payload.experience);
            void queryClient.invalidateQueries({
              queryKey: historyKeys.one(payload.experience.id),
            });
            invalidateModuleExperienceListQueries(queryClient);
            if (payload.experience.category === "experiment") {
              await queryClient.invalidateQueries({
                queryKey: experienceKeys.experimentList,
              });
            } else if (payload.experience.category === "personalization") {
              // On error recovery, invalidate relevant caches based on experience type
              void queryClient.invalidateQueries({
                queryKey: experienceKeys.detailedPersonalizationList,
              });
              void queryClient.invalidateQueries({
                queryKey: experienceKeys.homeOverviewPersonalizationList,
              });

              if (payload.experience.testTypes?.hasTestCampaign) {
                void queryClient.invalidateQueries({
                  queryKey: experienceKeys.offerPersonalizationList,
                });
              }
              if (payload.experience.testTypes?.hasTestCheckoutBlocks) {
                void queryClient.invalidateQueries({
                  queryKey: experienceKeys.checkoutPersonalizationList,
                });
              }
              if (payload.experience.testTypes?.hasTestPostPurchase) {
                void queryClient.invalidateQueries({
                  queryKey: experienceKeys.postPurchasePersonalizationList,
                });
              }
            }
          }
        }

        const message = getMutationErrorMessage(error);
        if (message) {
          if (error.response?.status === 503) {
            toast.info(message);
          } else {
            toast.error(message);
          }
        }
      }
    },
    onSettled: () => {
      setTimeout(() => dispatch(setIgnoreUpdateDetected(false)), 3000);
    },
  });
};

type ShippingSaveRecovery = {
  experienceId: string;
  startedAt: number;
  wasNew: boolean;
};

const shippingSaveRecoveryKey = (orgId: string, path: string) =>
  `shipping-save-recovery:${orgId}:${path}`;

const readShippingSaveRecovery = (
  orgId: string,
  path: string
): ShippingSaveRecovery | null => {
  try {
    const raw = sessionStorage.getItem(shippingSaveRecoveryKey(orgId, path));
    if (!raw) {
      return null;
    }
    const value: unknown = JSON.parse(raw);
    if (
      typeof value !== "object" ||
      value === null ||
      !("experienceId" in value) ||
      typeof value.experienceId !== "string" ||
      !("startedAt" in value) ||
      typeof value.startedAt !== "number" ||
      !("wasNew" in value) ||
      typeof value.wasNew !== "boolean"
    ) {
      return null;
    }
    return value as ShippingSaveRecovery;
  } catch {
    return null;
  }
};

const writeShippingSaveRecovery = (
  orgId: string,
  path: string,
  recovery: ShippingSaveRecovery
) => {
  sessionStorage.setItem(
    shippingSaveRecoveryKey(orgId, path),
    JSON.stringify(recovery)
  );
};

const clearShippingSaveRecovery = (
  orgId: string,
  path: string,
  experienceId: string
) => {
  if (readShippingSaveRecovery(orgId, path)?.experienceId === experienceId) {
    sessionStorage.removeItem(shippingSaveRecoveryKey(orgId, path));
  }
};

/**
 * Canonical "save the whole experience" flow shared by the experiment and
 * personalization pages and the visual builder: persists the shipping
 * progress-bar widget (when present) plus the experience itself. Reads fresh
 * store state at call time so a caller that mutates state in the same tick
 * (e.g. the visual builder committing its mods before saving) is captured.
 */
export const useSaveExperience = (options?: UsePutExperienceOptions) => {
  const putExperience = usePutExperience(options);
  const putWidget = usePutWidget("shippingProgressBar");
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const orgId = useGetOrg().data.org.id;
  const path = window.location.pathname;
  const recoveryAtMount = useRef(readShippingSaveRecovery(orgId, path));
  const [backgroundSaveId, setBackgroundSaveId] = useImmer<string | undefined>(
    recoveryAtMount.current?.experienceId
  );
  const [hasCheckedProcess, setHasCheckedProcess] = useImmer(false);
  const saveStartedAt = useRef(recoveryAtMount.current?.startedAt ?? 0);
  const saveInFlight = useRef(false);
  const lastCompletedSave = useSelector(
    (state) => state.experience.lastCompletedSave
  );
  const processes = useGetProcesses({
    enabled: Boolean(backgroundSaveId),
    refetchInterval: backgroundSaveId ? 5000 : false,
  });
  const processIsActive = processes.data?.processes.some(
    (process) => process.id === backgroundSaveId
  );
  const recovery = readShippingSaveRecovery(orgId, path);
  const processEndedWithoutEvent =
    Boolean(backgroundSaveId) && hasCheckedProcess && processIsActive === false;
  const completionEventReceived =
    Boolean(backgroundSaveId) &&
    lastCompletedSave?.experienceId === backgroundSaveId &&
    (lastCompletedSave?.at ?? 0) >= saveStartedAt.current;
  const completedSave = useQuery({
    queryKey: [
      "shipping-save-completion",
      backgroundSaveId,
      saveStartedAt.current,
    ],
    queryFn: () => getExperience(backgroundSaveId!),
    enabled: Boolean(
      backgroundSaveId &&
      (completionEventReceived ||
        (processEndedWithoutEvent && recovery?.wasNew))
    ),
    retry: false,
    refetchInterval: backgroundSaveId ? 5000 : false,
  });

  useEffect(() => {
    if (!backgroundSaveId) {
      return;
    }
    setHasCheckedProcess(false);
    void processes.refetch().then((result) => {
      if (result.isSuccess) {
        setHasCheckedProcess(true);
      }
    });
  }, [backgroundSaveId, processes.refetch, setHasCheckedProcess]);

  useEffect(() => {
    if (!backgroundSaveId || !completedSave.data) {
      return;
    }
    const { experience } = completedSave.data;
    setExperienceQueryData(queryClient, experience);
    invalidateModuleExperienceListQueries(queryClient);
    void queryClient.invalidateQueries({
      queryKey: experienceKeys.experimentList,
    });
    stateReInitHelper(experience, dispatch, undefined);
    dispatch(setHasClickedSave(false));
    saveInFlight.current = false;
    clearShippingSaveRecovery(orgId, path, backgroundSaveId);
    setBackgroundSaveId(undefined);
    toast.success("Test saved successfully");
  }, [
    backgroundSaveId,
    completedSave.data,
    dispatch,
    orgId,
    path,
    queryClient,
    setBackgroundSaveId,
  ]);

  useEffect(() => {
    if (
      !backgroundSaveId ||
      !processEndedWithoutEvent ||
      completionEventReceived
    ) {
      return;
    }
    if (recovery?.wasNew) {
      if (
        !axios.isAxiosError(completedSave.error) ||
        completedSave.error.response?.status !== 404
      ) {
        return;
      }
    }
    clearShippingSaveRecovery(orgId, path, backgroundSaveId);
    saveInFlight.current = false;
    setBackgroundSaveId(undefined);
    toast.error(
      recovery?.wasNew
        ? "The shipping save did not finish. Review the test before saving again."
        : "The shipping save could not be confirmed. Review the test before saving again."
    );
  }, [
    backgroundSaveId,
    completionEventReceived,
    completedSave.error,
    orgId,
    path,
    processEndedWithoutEvent,
    recovery?.wasNew,
    setBackgroundSaveId,
  ]);
  const experienceForValidation = useSelector(selectedExperience);
  const shippingSelections =
    experienceForValidation?.shippingTestMethodDefinitions ?? [];
  const marketValidation = useMarketSelections(
    shippingSelections,
    shippingSelections.some(
      (selection) => selection.shippingModel === "markets"
    ),
    experienceForValidation?.status === "started"
      ? experienceForValidation.id
      : undefined
  );

  const saveExperience = () => {
    if (
      saveInFlight.current ||
      putExperience.isPending ||
      backgroundSaveId ||
      readShippingSaveRecovery(orgId, path) !== null
    ) {
      return;
    }
    const state = store.getState();
    const experience = selectedExperience(state);
    const selectionIssue =
      experience?.status === "ended"
        ? null
        : marketValidation.saveBlockReason(
            experience?.shippingTestMethodDefinitions ?? []
          );
    if (selectionIssue) {
      toast.error(selectionIssue);
      return;
    }

    if (experience?.type === "shipping") {
      const progressBarWidget = selectedWidget(state);
      if (progressBarWidget) {
        putWidget.mutate(progressBarWidget);
      }
    }

    const payload = getPutExperiencePayload(state);
    if (!payload) {
      return;
    }
    const wasNew = !payload.experience.id;

    if (experience?.type === "shipping") {
      const shippingSaveId =
        payload.experience.id ?? state.experience.shippingSaveId ?? v4();
      payload.experience.id = shippingSaveId;
      dispatch(setShippingSaveId(shippingSaveId));
      saveStartedAt.current = Date.now();
    }

    saveInFlight.current = true;
    return putExperience
      .mutateAsync({
        ...payload,
        containsNewVisualBuilderMod: state.onsiteEdits.hasNewVisualBuilderMod,
      })
      .then((result) => {
        saveInFlight.current = false;
        if (experience?.type === "shipping" && payload.experience.id) {
          clearShippingSaveRecovery(orgId, path, payload.experience.id);
        }
        return result;
      })
      .catch((error: unknown) => {
        if (
          experience?.type === "shipping" &&
          axios.isAxiosError(error) &&
          error.response?.status === 503
        ) {
          writeShippingSaveRecovery(orgId, path, {
            experienceId: payload.experience.id!,
            startedAt: saveStartedAt.current,
            wasNew,
          });
          setBackgroundSaveId(payload.experience.id);
        } else {
          saveInFlight.current = false;
        }
        throw error;
      });
  };

  return {
    saveExperience,
    isSaving: putExperience.isPending || Boolean(backgroundSaveId),
  };
};

export const useUploadProductsToS3 = () => {
  const { mutateAsync: getPresignedUrl } = useGetS3PresignedUploadUrl();
  const { mutateAsync: uploadToUrl } = useUploadToPresignedUrl();

  return useMutation({
    mutationFn: async ({
      products,
    }: {
      products: (
        | ExperienceProductDto
        | OfferProductDto
        | AnalyticsProductDto
      )[];
      category: ExperienceCategory;
    }): Promise<S3FileLocationDto> => {
      const body = JSON.stringify(
        products.filter((tp) => tp.variants.length > 0)
      );

      const { presignedUrl, bucket, key } =
        await getPresignedUrl("product-upload");

      await uploadToUrl({
        presignedUrl,
        body,
        contentType: "application/json",
      });

      return { bucket, key };
    },
    onError: (err, { category }) => {
      toast.error(
        `Error, Unable to Save ${officialCategoryName(category, true)}`
      );
      Sentry.captureException(err);
      Sentry.captureMessage("Unable to upload file to s3");
    },
  });
};

function sanitizeData(data: PutExperienceRequestBodyType) {
  if (data.experiencePageTargeting) {
    for (const targeting of data.experiencePageTargeting) {
      if (targeting.filterType === "url" && targeting.value) {
        targeting.value = targeting.value.trim();
      }
    }
  }
}
